import React, { useState, useMemo } from "react";
import { Button, Column, Row, Select, SelectOption, Sheet, Text } from "@amzn/meridian";
import { useAppSelector } from "src/store/store";

type StreamingConfigDialogProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: (config: { resolutionScale: number; quality: number }) => void;
};

const StreamingConfigDialog = ({
  open,
  onClose,
  onConfirm,
}: StreamingConfigDialogProps) => {
  const { screenWidth, screenHeight } = useAppSelector((state) => state.inspectionCapture);
  
  const [resolutionScale, setResolutionScale] = useState(0.5); // Default to 50%
  const [quality, setQuality] = useState(0.6);

  const handleConfirm = () => {
    onConfirm({ resolutionScale, quality });
    onClose();
  };

  // Format resolution option labels with calculated dimensions
  const getResolutionLabel = (scale: number, label: string) => {
    if (screenWidth === 0 || screenHeight === 0) {
      return label;
    }
    const width = Math.round(screenWidth * scale);
    const height = Math.round(screenHeight * scale);
    return `${label} (${width}x${height})`;
  };

  return (
    <Sheet
      className="streaming-config-dialog"
      type="overlay"
      side="bottom"
      open={open}
      onClose={onClose}
    >
      <div style={{ borderRadius: "21px" }}>
        <Column spacing="400">
          <Text type="h400">Configure Streaming Settings</Text>

          <Column spacing="300">
            <Column spacing="100">
              <Text type="b200">Resolution</Text>
              {screenWidth > 0 && screenHeight > 0 && (
                <Text type="b100" color="secondary">
                  Screen size: {screenWidth}x{screenHeight}
                </Text>
              )}
              <Select
                value={resolutionScale.toString()}
                onChange={(value) => setResolutionScale(parseFloat(value))}
              >
                <SelectOption 
                  value="0.3" 
                  label={getResolutionLabel(0.3, "30% - Low")} 
                />
                <SelectOption 
                  value="0.5" 
                  label={getResolutionLabel(0.5, "50% - Medium")} 
                />
                <SelectOption 
                  value="0.8" 
                  label={getResolutionLabel(0.8, "80% - High")} 
                />
                <SelectOption 
                  value="1.0" 
                  label={getResolutionLabel(1.0, "100% - Maximum")} 
                />
              </Select>
            </Column>

            <Column spacing="100">
              <Row alignmentHorizontal="justify">
                <Text type="b200">Quality</Text>
                <Text type="b200">{(quality * 100).toFixed(0)}%</Text>
              </Row>
              <input
                type="range"
                min="0.3"
                max="1.0"
                step="0.1"
                value={quality}
                onChange={(e) => setQuality(parseFloat(e.target.value))}
                style={{
                  width: "100%",
                  height: "4px",
                  borderRadius: "2px",
                  background: "rgba(0, 115, 187, 0.2)",
                  outline: "none",
                  WebkitAppearance: "none",
                }}
              />
            </Column>

            <Column spacing="100" spacingInset="200 none none">
              <Text type="b100" color="secondary">
                Resolution is based on your screen size ({screenWidth}x{screenHeight}). 
                Higher resolution and quality provide better detection accuracy but use more bandwidth. 
                Medium settings (50%) are recommended for most scenarios.
              </Text>
            </Column>
          </Column>

          <Row
            alignmentHorizontal="justify"
            widths="fill"
            spacingInset="400 none none"
          >
            <Button onClick={onClose} type="tertiary">
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              type="primary"
            >
              Start Streaming
            </Button>
          </Row>
        </Column>
      </div>
    </Sheet>
  );
};

export default StreamingConfigDialog;
